import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SettingsMenu.css';

const STORAGE_KEY = 'vexel_audio_settings';

const DEFAULTS = {
  musicEnabled: true,
  musicVolume: 0.7, // 0..1
  sfxEnabled: true,
  sfxVolume: 0.8, // 0..1
};

// Tasti gestiti in MainScene (keydown-L / C / E, SPACE) e UIScene.
const KEYBOARD_CONTROLS = [
  { keys: ['MOUSE'], action: 'AIM / STEER' },
  { keys: ['SPACE'], action: 'BOOST' },
  { keys: ['L'], action: 'LEADERBOARD' },
  { keys: ['E'], action: 'USE POWER-UP' },
  { keys: ['C'], action: 'CASH OUT' },
];

// Su touch i tasti non esistono: mostrare "SPACE = BOOST" a un giocatore mobile
// e' peggio che non mostrare niente.
const TOUCH_CONTROLS = [
  { keys: ['STICK'], action: 'AIM / STEER' },
  { keys: ['BOOST'], action: 'HOLD TO BOOST' },
  { keys: ['TAP'], action: 'LEADERBOARD BAR' },
  { keys: ['TAP'], action: 'BAG = POWER-UP' },
  { keys: ['TAP'], action: 'CASH OUT' },
];

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {
    return { ...DEFAULTS };
  }
}

const SettingsMenu = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);
  const panelRef = useRef(null);
  const controls = isTouchDevice() ? TOUCH_CONTROLS : KEYBOARD_CONTROLS;

  // Propaga le impostazioni al gioco (MainScene -> Synthesizer) e le salva.
  const broadcast = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {}
    window.dispatchEvent(new CustomEvent('vexel_audio_settings', { detail: next }));
  }, []);

  // Al mount, invia lo stato iniziale (nel caso la scena sia gia' pronta).
  useEffect(() => {
    broadcast(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      broadcast(next);
      return next;
    });
  };

  // Chiudi cliccando fuori dal pannello.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('touchstart', onDown);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  return (
    <>
      {/* Velo trasparente sopra il canvas (ma sotto il pannello): intercetta il click
          "fuori" che chiude le impostazioni, cosi' non arriva al gioco come boost. */}
      {open && (
        <div
          className="vx-backdrop"
          onPointerDown={(e) => { e.preventDefault(); setOpen(false); }}
        />
      )}
      <div className="vx-settings" ref={panelRef}>
      <button
        className={`vx-gear ${open ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Audio settings"
        title="Settings"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            fill="currentColor"
            d="M19.14 12.94a7.49 7.49 0 0 0 .05-.94 7.49 7.49 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7 7 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54a7 7 0 0 0-1.62.94l-2.39-.96a.5.5 0 0 0-.6.22L2.33 8.8a.5.5 0 0 0 .12.64l2.03 1.58a7.49 7.49 0 0 0 0 1.88l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96a7 7 0 0 0 1.62.94l.36 2.54a.5.5 0 0 0 .5.42h3.84a.5.5 0 0 0 .5-.42l.36-2.54a7 7 0 0 0 1.62-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z"
          />
        </svg>
      </button>

      {open && (
        <div className="vx-panel glass-panel">
          <div className="vx-panel-header">
            <span className="vx-panel-title">AUDIO</span>
            <span className="vx-panel-sub">SYSTEM CONFIG</span>
          </div>

          {/* MUSIC */}
          <div className={`vx-row ${settings.musicEnabled ? '' : 'muted'}`}>
            <div className="vx-row-top">
              <span className="vx-label">▸ MUSIC</span>
              <button
                className={`vx-toggle ${settings.musicEnabled ? 'on' : 'off'}`}
                onClick={() => update({ musicEnabled: !settings.musicEnabled })}
              >
                <span className="vx-toggle-knob" />
                <span className="vx-toggle-text">{settings.musicEnabled ? 'ON' : 'OFF'}</span>
              </button>
            </div>
            <input
              className="vx-slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(settings.musicVolume * 100)}
              disabled={!settings.musicEnabled}
              onChange={(e) => update({ musicVolume: Number(e.target.value) / 100 })}
            />
          </div>

          {/* SFX */}
          <div className={`vx-row ${settings.sfxEnabled ? '' : 'muted'}`}>
            <div className="vx-row-top">
              <span className="vx-label">▸ SFX</span>
              <button
                className={`vx-toggle ${settings.sfxEnabled ? 'on' : 'off'}`}
                onClick={() => update({ sfxEnabled: !settings.sfxEnabled })}
              >
                <span className="vx-toggle-knob" />
                <span className="vx-toggle-text">{settings.sfxEnabled ? 'ON' : 'OFF'}</span>
              </button>
            </div>
            <input
              className="vx-slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(settings.sfxVolume * 100)}
              disabled={!settings.sfxEnabled}
              onChange={(e) => update({ sfxVolume: Number(e.target.value) / 100 })}
            />
          </div>

          {/* CONTROLS */}
          <div className="vx-section">
            <div className="vx-section-title">▸ CONTROLS</div>
            <ul className="vx-keys">
              {controls.map(({ keys, action }) => (
                <li className="vx-key-row" key={action}>
                  <span className="vx-key-caps">
                    {keys.map((k) => (
                      <kbd className="vx-kbd" key={k}>{k}</kbd>
                    ))}
                  </span>
                  <span className="vx-key-action">{action}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default SettingsMenu;
